import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink, Loader2, Plus } from "lucide-react";
import { Link } from "@tanstack/react-router";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { useAuth } from "@/contexts/AuthContext";
import { portalFetch } from "@/lib/portalApi";
import { platformApi } from "@/services/api/platform";

import { useConnectorPropertyDefinitions } from "@/hooks/useConnectorPropertyDefinitions";
import { usePermissions } from "@/hooks/usePermissions";
import type { PortalApiError } from "@/lib/portalApi";

import { ConnectorPropertyFields } from "./ConnectorPropertyFields";
import { SqlConnectorCreateForm } from "./SqlConnectorCreateForm";
import { FtpConfigFields, type FtpConnectionValues } from "./FtpConfigFields";
import { slugToApiType, formatTypeName } from "@/lib/connectorTypes";
import { isOAuthConnector, buildConnectorAuthoriseUrl } from "@/lib/connectorOAuth";
import {
  SALESFORCE_AUTH_MODE,
  SALESFORCE_CLIENT_CREDENTIALS,
  SALESFORCE_CLIENT_CREDENTIALS_PROPERTIES,
} from "@/lib/salesforceAuthMode";

/**
 * Auto-subscribe org to a connector service after connector creation.
 * Each connector type is its own service (e.g., "xero", "cin7").
 * Creates a per-instance subscription when clientConnectorId is provided.
 * Silently succeeds if subscription already exists.
 */
// eslint-disable-next-line react-refresh/only-export-components
export async function autoSubscribeConnector(
  token: string,
  organisationId: string,
  connectorTypeSlug: string,
  clientConnectorId?: number,
): Promise<void> {
  try {
    const services = await platformApi.listPlatformServices(token, false);
    // Each connector type is its own service — match by slug name
    const slug = connectorTypeSlug.replace(/_connector$/, "");
    const connectorSvc = services.find(
      (s) => s.category === "connector" && s.name === slug,
    );
    if (!connectorSvc) return;

    await platformApi.addServiceSubscription(token, organisationId, {
      service_name: connectorSvc.name,
      tier: "standard",
      client_connector_id: clientConnectorId,
    });
  } catch {
    // Subscription may already exist — that's fine
  }
}

type ConnectorCreateFormProps = {
  typeSlug: string;
};

// Salesforce's own setup guide for the flow the "Integration user" option runs.
const SALESFORCE_CLIENT_CREDENTIALS_HELP_URL =
  "https://help.salesforce.com/s/articleView?id=xcloud.configure_client_credentials_flow_for_external_client_apps.htm&type=5";

/**
 * The message shown when the create POST fails. dp answers a Salesforce
 * Integration user create with two structured errors the user can act on; every
 * other failure keeps portalFetch's message unchanged.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function createErrorMessage(err: unknown): string {
  if (err instanceof Error && "status" in err && "details" in err) {
    const { status, details } = err as PortalApiError;
    const detail = (details as { detail?: unknown } | undefined)?.detail;
    const d =
      detail && typeof detail === "object"
        ? (detail as Record<string, unknown>)
        : undefined;
    if (status === 409 && d?.code === "connector_exists") {
      // The rule spans every organisation in Lara, so the existing connector may not
      // be one this user can see — say what is true, not where to find it.
      return "This Salesforce org is already connected to Lara, so a second connector can't be created for it.";
    }
    if (status === 422 && d?.code === "salesforce_rejected") {
      const code =
        typeof d.salesforce_error === "string" ? ` (${d.salesforce_error})` : "";
      const message = typeof d.message === "string" ? d.message : err.message;
      return `${message}${code}`;
    }
  }
  return err instanceof Error ? err.message : "An error occurred";
}

/** https and a host ending .my.salesforce.com — the engine probe's rule. */
// eslint-disable-next-line react-refresh/only-export-components
export function isMyDomainUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return (
      url.protocol === "https:" &&
      url.hostname.toLowerCase().endsWith(".my.salesforce.com")
    );
  } catch {
    return false;
  }
}

const DEFAULT_FTP_VALUES: FtpConnectionValues = {
  host: "",
  username: "",
  password: "",
  path: "",
  processedPath: "",
  filter: "",
  excludeFilter: "",
};

export function ConnectorCreateForm({ typeSlug }: ConnectorCreateFormProps) {
  // SqlServer and MySql share the specialised form — source SQL config +
  // target selector + table discovery — with the backend dispatching on
  // sourceType.
  if (typeSlug === "sqlserver") {
    return <SqlConnectorCreateForm sourceType="SqlServer" />;
  }
  if (typeSlug === "mysql") {
    return <SqlConnectorCreateForm sourceType="MySql" />;
  }

  return <GenericConnectorCreateForm typeSlug={typeSlug} />;
}

function GenericConnectorCreateForm({ typeSlug }: { typeSlug: string }) {
  const navigate = useNavigate();
  const { selectedOrganisation } = usePortalData();
  const { token } = useAuth();

  const apiType = slugToApiType(typeSlug);
  const typeName = formatTypeName(typeSlug);
  const isFtp = typeSlug.toLowerCase() === "ftp";
  const isXero = typeSlug.toLowerCase() === "xero";
  const isSalesforce = typeSlug.toLowerCase() === "salesforce";
  const { can } = usePermissions();

  // Salesforce offers two ways in: "Connect with Salesforce" (the OAuth
  // handshake, the default and unchanged) or "Integration user" — the client's
  // own External Client App running the Client Credentials flow, created here
  // from three fields. The second option creates a connector directly, so it is
  // offered only to someone who may create connectors.
  const canChooseIntegrationUser = isSalesforce && can("create", "connectors");
  const [salesforceMode, setSalesforceMode] = useState<
    "oauth" | typeof SALESFORCE_CLIENT_CREDENTIALS
  >("oauth");
  const isIntegrationUser =
    canChooseIntegrationUser && salesforceMode === SALESFORCE_CLIENT_CREDENTIALS;

  // Whether this connector's "add" flow is an OAuth handshake (a redirect to B2C)
  // rather than a name/property form. Explicit allow-list — see connectorOAuth.ts
  // for why this is not derived from the type's RequiresAuthorisation flag.
  const isOAuth = isOAuthConnector(typeSlug) && !isIntegrationUser;

  const {
    definitions,
    loading: defsLoading,
    failed: defsFailed,
  } = useConnectorPropertyDefinitions(apiType);

  // An Integration user renders only its three credential fields. dp registers
  // them optional (an OAuth connector has none of them), so they are made
  // required here; dp enforces the same on create. The readonly mode definition
  // is not a field — the form sets it.
  const typeDefinitions = isSalesforce
    ? definitions
        .filter((d) => SALESFORCE_CLIENT_CREDENTIALS_PROPERTIES.includes(d.name))
        .map((d) => ({ ...d, required: true }))
    : definitions;
  const hasTypeProperties = !isFtp && !isOAuth && typeDefinitions.length > 0;

  // A failed definitions read is only load-bearing for types that would have
  // rendered credential fields. FTP has its own hard-coded form and OAuth never
  // collects properties here, so for those an unreachable endpoint changes
  // nothing and must not block the user.
  //
  // An Integration user with NO Salesforce definitions is the same dead end: the
  // three fields are what dp needs, and a form without them would POST nothing to
  // probe.
  const defsUnavailable =
    (defsFailed && !isFtp && !isOAuth) ||
    (isIntegrationUser && !defsLoading && typeDefinitions.length === 0);

  const [connectorName, setConnectorName] = useState("");
  const [propertyValues, setPropertyValues] = useState<Record<string, string>>({});
  const [ftpConfig, setFtpConfig] = useState<FtpConnectionValues>(DEFAULT_FTP_VALUES);
  const [submitting, setSubmitting] = useState(false);

  // Set true while we navigate the browser to B2C — gives the button
  // a "Redirecting…" state until the page actually unloads.
  const [authorising, setAuthorising] = useState(false);

  const handleOAuthAuthorise = () => {
    if (!selectedOrganisation) {
      toast.error("No organisation selected");
      return;
    }
    setAuthorising(true);

    // B2C handles the full OAuth dance (sign-in, any tenant/org pick, token
    // storage, connector record creation) and 302s back to the portal landing
    // page when done. Full-page redirect — not a popup. The shared builder is
    // also used by the connector card's re-authorise action so the two flows
    // stay identical. See buildConnectorAuthoriseUrl in connectorOAuth.ts.
    const url = buildConnectorAuthoriseUrl({
      typeSlug,
      state: selectedOrganisation.organisationId,
      redirectOrigin: window.location.origin,
    });
    window.location.replace(url);
  };

  const handleFtpConfigChange = (
    field: keyof FtpConnectionValues,
    value: string,
  ) => {
    setFtpConfig((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = (): string | null => {
    // Checked FIRST and before anything else: if the definitions read failed we
    // do not know what this connector requires, so every other check below is
    // vacuous. Submitting would POST without credentials and 422 server-side
    // with nothing on screen to correct.
    if (defsUnavailable) {
      return `Could not load the configuration fields for ${typeName}. Reload the page and try again — creating the connector now would fail.`;
    }
    if (!connectorName.trim()) {
      return "Connector name is required";
    }
    if (isFtp) {
      if (!ftpConfig.host || !ftpConfig.username || !ftpConfig.password || !ftpConfig.path) {
        return "FTP host, username, password, and path are required";
      }
    }
    if (isIntegrationUser) {
      // Same rule as the engine's probe (SalesforceClientCredentials.IsMyDomainHost):
      // integration-user tokens are issued only on the org's My Domain host.
      const loginUrl = propertyValues["salesforce.loginUrl"]?.trim();
      if (loginUrl && !isMyDomainUrl(loginUrl)) {
        return "My Domain URL must look like https://yourcompany.my.salesforce.com (Salesforce: Setup → My Domain).";
      }
    }
    if (hasTypeProperties) {
      for (const def of typeDefinitions) {
        if (def.required && !propertyValues[def.name]?.trim()) {
          return `${def.display_name} is required`;
        }
      }
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    if (!selectedOrganisation) {
      toast.error("No organisation selected");
      return;
    }

    setSubmitting(true);

    try {
      // Build properties for source-specific config
      const properties: Record<string, string> = {};

      if (isFtp) {
        properties["ftp.host"] = ftpConfig.host;
        properties["ftp.username"] = ftpConfig.username;
        properties["ftp.password"] = ftpConfig.password;
        properties["ftp.path"] = ftpConfig.path;
        if (ftpConfig.processedPath)
          properties["ftp.processedPath"] = ftpConfig.processedPath;
        if (ftpConfig.filter) properties["ftp.filter"] = ftpConfig.filter;
        if (ftpConfig.excludeFilter)
          properties["ftp.excludeFilter"] = ftpConfig.excludeFilter;
      }

      // Include type-specific properties from dynamic fields
      if (hasTypeProperties) {
        for (const [key, value] of Object.entries(propertyValues)) {
          if (value) properties[key] = value;
        }
      }

      if (isIntegrationUser) {
        properties[SALESFORCE_AUTH_MODE] = SALESFORCE_CLIENT_CREDENTIALS;
      }

      const requestBody: Record<string, unknown> = {
        name: connectorName.trim(),
        sourceConnectorType: apiType,
      };

      // Only include properties if we have some
      if (Object.keys(properties).length > 0) {
        requestBody.properties = properties;
      }

      const created = await portalFetch<{ Id: number; Name: string }>(
        `/api/v1/gateway/business2cloud/api/v1/connectors/org/${selectedOrganisation.organisationId}`,
        {
          token: token ?? undefined,
          method: "POST",
          body: requestBody,
        },
      );

      // Auto-subscribe: create per-instance item subscription
      if (token) {
        await autoSubscribeConnector(
          token,
          selectedOrganisation.organisationId,
          typeSlug,
          created?.Id,
        );
      }

      toast.success("Connector created successfully");
      navigate({
        to: "/portal/connectors/$typeSlug",
        params: { typeSlug },
      });
    } catch (err) {
      toast.error(createErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!selectedOrganisation) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
        No organisation selected.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/portal/connectors/$typeSlug"
          params={{ typeSlug }}
          className="flex items-center gap-2 text-sm text-primary transition hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to {typeName} Connectors
        </Link>
      </div>

      <div>
        <h1 className="text-3xl font-bold text-slate-900">
          Create {typeName} Connector
        </h1>
        <p className="mt-2 text-slate-600">
          Configure a new {typeName} connector for your organisation
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {canChooseIntegrationUser && (
          <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">
              How should Lara sign in to Salesforce?
            </h2>
            <div
              role="radiogroup"
              aria-label="Salesforce sign-in"
              className="mt-4 grid gap-3 sm:grid-cols-2"
            >
              {(
                [
                  {
                    value: "oauth",
                    title: "Connect with Salesforce",
                    body: "Sign in to Salesforce and authorise Lara. Best for a quick start.",
                  },
                  {
                    value: SALESFORCE_CLIENT_CREDENTIALS,
                    title: "Integration user",
                    body: "Use your own External Client App and an API-only integration user. Nobody's personal login is involved.",
                  },
                ] as const
              ).map((option) => {
                const selected = salesforceMode === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setSalesforceMode(option.value)}
                    disabled={submitting || authorising}
                    className={
                      selected
                        ? "rounded-2xl border-2 border-primary bg-primary/5 p-4 text-left"
                        : "rounded-2xl border border-slate-200 p-4 text-left hover:border-slate-300"
                    }
                  >
                    <span className="block font-medium text-slate-900">
                      {option.title}
                    </span>
                    <span className="mt-1 block text-sm text-slate-500">
                      {option.body}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Connector Name — not shown for OAuth types (B2C names the connector
            from the authorised org on callback) */}
        {!isOAuth && (
          <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">
              Connector Details
            </h2>
            <div className="mt-4 space-y-2">
              <Label htmlFor="connector-name">Connector Name</Label>
              <Input
                id="connector-name"
                placeholder={`e.g., ${typeName} for ${selectedOrganisation.organisationName || "my company"}`}
                value={connectorName}
                onChange={(e) => setConnectorName(e.target.value)}
                disabled={submitting}
              />
              <p className="text-xs text-slate-500">
                A descriptive name to identify this connector
              </p>
            </div>
          </div>
        )}

        {/* OAuth authorisation — B2C handles sign-in, any tenant/org pick,
            token storage, connector record creation, then redirects back to the
            portal landing page. Nothing else on this form is needed. */}
        {isOAuth && (
          <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">
              {typeName} Authorisation
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              You'll be redirected to {typeName} to sign in and authorise
              access. We'll bring you back when it's done.
            </p>

            <div className="mt-6">
              <Button
                type="button"
                onClick={handleOAuthAuthorise}
                disabled={authorising}
                className={isXero ? "bg-[#13B5EA] hover:bg-[#0e9ac8]" : undefined}
              >
                {authorising ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Redirecting...
                  </>
                ) : (
                  <>
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Connect to {typeName}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* FTP-specific source config */}
        {isFtp && (
          <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">
              FTP Source Configuration
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Connection details for the FTP server to read data from
            </p>
            <div className="mt-6">
              <FtpConfigFields
                values={ftpConfig}
                onChange={handleFtpConfigChange}
                disabled={submitting}
              />
            </div>
          </div>
        )}

        {/* Type-specific property fields from definitions */}
        {hasTypeProperties && !defsLoading && (
          <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">
              {typeName} Configuration
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Enter the credentials and settings for this connector
            </p>
            {isIntegrationUser && (
              <p className="mt-2 text-sm text-slate-500">
                In Salesforce, create an External Client App with the Client
                Credentials flow enabled, set its Run As user to your
                integration user, and give that user read access to the objects
                Lara should sync.{" "}
                <a
                  href={SALESFORCE_CLIENT_CREDENTIALS_HELP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  Salesforce: Configure the Client Credentials Flow for External
                  Client Apps
                </a>
              </p>
            )}
            <div className="mt-6">
              <ConnectorPropertyFields
                definitions={typeDefinitions}
                values={propertyValues}
                onChange={(name, value) =>
                  setPropertyValues((prev) => ({ ...prev, [name]: value }))
                }
                disabled={submitting}
              />
            </div>
          </div>
        )}

        {/* The definitions read failed, so we cannot say what this connector
            needs. Must be rendered INSTEAD OF the "What happens next?" panel
            below, never alongside it — that panel says the opposite of the
            truth here ("you can configure this later"), which is what made the
            original dead end so hard to spot. */}
        {defsUnavailable && !defsLoading && (
          <div
            role="alert"
            className="rounded-3xl border border-red-200 bg-red-50/50 p-6 shadow-sm"
          >
            <h3 className="text-lg font-semibold text-slate-900">
              Configuration fields could not be loaded
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              We could not reach the service that describes what {typeName}{" "}
              needs, so this form cannot show its credential fields. Creating the
              connector now would fail. Please reload the page and try again — if
              it keeps happening, contact support.
            </p>
          </div>
        )}

        {/* Info section for types with no property definitions (skip for OAuth) */}
        {!isFtp && !isOAuth && !hasTypeProperties && !defsLoading && !defsUnavailable && (
          <div className="rounded-3xl border border-blue-200 bg-blue-50/50 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900">
              What happens next?
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              After creating this connector, you may need to configure additional
              properties such as API keys or connection credentials. These can be
              configured from the connector detail page.
            </p>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-4">
          <Link
            to="/portal/connectors/$typeSlug"
            params={{ typeSlug }}
          >
            <Button type="button" variant="outline" disabled={submitting}>
              Cancel
            </Button>
          </Link>
          {!isOAuth && (
            <Button type="submit" disabled={submitting || defsUnavailable}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Connector
                </>
              )}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
